import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, FileText, Link as LinkIcon, Video, X, Upload, Check } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResourcesPage({ admin, onLogout }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFilename, setUploadedFilename] = useState('');
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    resource_type: 'document',
    category: '',
    file_url: '',
    price: '',
    is_for_sale: false,
    status: 'draft',
  });

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        price: formData.price === '' ? 0 : parseFloat(formData.price),
      };
      if (editingId) {
        await axios.put(`${API}/resources/${editingId}`, payload);
      } else {
        await axios.post(`${API}/resources`, payload);
      }
      fetchResources();
      resetForm();
    } catch (error) {
      console.error('Error saving resource:', error);
      alert('Erreur lors de l\'enregistrement');
    }
  };

  const startEdit = (resource) => {
    setFormData({
      title: resource.title || '',
      description: resource.description || '',
      resource_type: resource.resource_type || 'document',
      category: resource.category || '',
      file_url: resource.file_url || '',
      price: resource.price ?? '',
      is_for_sale: !!resource.is_for_sale,
      status: resource.status || 'draft',
    });
    setUploadedFilename('');
    setEditingId(resource.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette ressource ?')) return;
    try {
      await axios.delete(`${API}/resources/${id}`);
      fetchResources();
    } catch (error) {
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      resource_type: 'document',
      category: '',
      file_url: '',
      price: '',
      is_for_sale: false,
      status: 'draft',
    });
    setEditingId(null);
    setShowForm(false);
    setUploadedFilename('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      alert('Fichier trop volumineux (max 50 Mo).');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await axios.post(`${API}/uploads`, data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const fullUrl = `${BACKEND_URL}${response.data.url}`;
      setFormData(prev => ({ ...prev, file_url: fullUrl }));
      setUploadedFilename(response.data.filename);
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.detail || 'Erreur lors du téléversement');
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const getResourceIcon = (type) => {
    switch(type) {
      case 'video': return <Video size={20} className="text-violet-500" />;
      case 'link': return <LinkIcon size={20} className="text-violet-500" />;
      default: return <FileText size={20} className="text-violet-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const badges = {
      document: 'bg-blue-100 text-blue-700',
      video: 'bg-purple-100 text-purple-700',
      link: 'bg-green-100 text-green-700',
      other: 'bg-gray-100 text-gray-700'
    };
    
    const labels = {
      document: 'Document',
      video: 'Vidéo',
      link: 'Lien',
      other: 'Autre'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badges[type]}`}>
        {labels[type]}
      </span>
    );
  };

  return (
    <AdminLayout admin={admin} onLogout={onLogout} title="Gestion des Ressources Pédagogiques">
      <div className="space-y-6">
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 btn-violet"
            data-testid="add-resource-button"
          >
            <Plus size={20} />
            Nouvelle Ressource
          </button>
        </div>

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  {editingId ? 'Modifier la ressource' : 'Nouvelle ressource'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Titre *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="input-violet"
                      required
                      data-testid="resource-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      className="input-violet"
                      rows="3"
                      required
                      data-testid="resource-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type *</label>
                      <select
                        value={formData.resource_type}
                        onChange={(e) => setFormData({...formData, resource_type: e.target.value})}
                        className="input-violet"
                        required
                        data-testid="resource-type-select"
                      >
                        <option value="document">Document</option>
                        <option value="video">Vidéo</option>
                        <option value="link">Lien</option>
                        <option value="other">Autre</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie *</label>
                      <input
                        type="text"
                        value={formData.category}
                        onChange={(e) => setFormData({...formData, category: e.target.value})}
                        className="input-violet"
                        placeholder="Ex: Santé, Management..."
                        required
                        data-testid="resource-category-input"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Prix (€)</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        className="input-violet"
                        placeholder="0.00"
                        data-testid="resource-price-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">Laissez à 0 si la ressource est gratuite.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Statut</label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({...formData, status: e.target.value})}
                        className="input-violet"
                        data-testid="resource-status-select"
                      >
                        <option value="draft">Brouillon (non visible)</option>
                        <option value="published">Publiée</option>
                      </select>
                    </div>
                  </div>

                  <label className="flex items-center gap-3 p-4 rounded-lg border-2 border-violet-100 hover:bg-violet-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_for_sale}
                      onChange={(e) => setFormData({...formData, is_for_sale: e.target.checked})}
                      className="w-5 h-5 accent-violet-600"
                      data-testid="resource-for-sale-checkbox"
                    />
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Mettre en vente sur la boutique publique</div>
                      <div className="text-xs text-gray-500">La ressource apparaîtra sur /ressources si elle est aussi publiée.</div>
                    </div>
                  </label>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fichier de la ressource
                    </label>

                    {/* Upload button */}
                    <div className="mb-3">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.zip"
                        onChange={handleFileSelect}
                        className="hidden"
                        data-testid="resource-file-input"
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-violet-300 rounded-lg text-violet-600 hover:bg-violet-50 hover:border-violet-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="upload-file-button"
                      >
                        {uploading ? (
                          <>
                            <div className="animate-spin h-5 w-5 border-2 border-violet-600 border-t-transparent rounded-full"></div>
                            <span>Téléversement en cours...</span>
                          </>
                        ) : uploadedFilename ? (
                          <>
                            <Check size={20} className="text-green-500" />
                            <span className="truncate">{uploadedFilename}</span>
                            <span className="text-xs text-gray-500">(Changer)</span>
                          </>
                        ) : (
                          <>
                            <Upload size={20} />
                            <span>Téléverser un fichier (PDF, Word, PPT, image…)</span>
                          </>
                        )}
                      </button>
                      <p className="text-xs text-gray-500 mt-1">
                        Formats acceptés : PDF, Word, PowerPoint, Excel, images, vidéos MP4. Max 50 Mo.
                      </p>
                    </div>

                    <div className="flex items-center gap-3 my-2">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <span className="text-xs text-gray-400 uppercase">ou</span>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>

                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      URL externe (YouTube, Google Drive, etc.)
                    </label>
                    <input
                      type="url"
                      value={formData.file_url}
                      onChange={(e) => {
                        setFormData({...formData, file_url: e.target.value});
                        setUploadedFilename('');
                      }}
                      className="input-violet"
                      placeholder="https://..."
                      data-testid="resource-url-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="btn-violet flex-1" data-testid="save-resource-button">
                    {editingId ? 'Mettre à jour' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : resources.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 mb-4">Aucune ressource</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {resources.map((resource) => (
              <div key={resource.id} className="card-violet" data-testid={`resource-card-${resource.id}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="p-3 bg-violet-100 rounded-lg">
                    {getResourceIcon(resource.resource_type)}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                      {resource.title}
                    </h3>
                    {getTypeBadge(resource.resource_type)}
                  </div>
                </div>

                <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                  {resource.description}
                </p>

                <div className="mb-4">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                    {resource.category}
                  </span>
                </div>

                {resource.file_url && (
                  <a
                    href={resource.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-violet-600 hover:underline mb-4 block truncate"
                  >
                    Accéder à la ressource →
                  </a>
                )}

                <div className="flex gap-2 pt-4 border-t border-gray-100">
                  <button
                    onClick={() => startEdit(resource)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-violet-50 text-violet-600 rounded-lg hover:bg-violet-100 transition-colors"
                    data-testid={`edit-resource-${resource.id}`}
                  >
                    <Edit size={16} />
                    Modifier
                  </button>
                  <button
                    onClick={() => handleDelete(resource.id)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    data-testid={`delete-resource-${resource.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}